import React, { useRef, useState } from 'react';
import { matchNamibianTown, suggestNamibianTowns } from '../../utils/namibianTowns';

// A Namibian town, typed with suggestions: the towns that match show under
// the box as the user types, and one is picked by click or the keyboard.
// Only a town on the list counts; anything else is marked in red (the form
// refuses it on submit).
const TownInput = ({ value, onChange, placeholder = 'Start typing your town', className = '', id }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef(null);
  const suggestions = suggestNamibianTowns(value);
  const valid = Boolean(matchNamibianTown(value));
  const choose = (town) => { onChange(town); setOpen(false); };

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        type="text"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Let a click on a suggestion land first; an exact name (any case or
          // accent) becomes the listed spelling.
          blurTimer.current = setTimeout(() => {
            setOpen(false);
            const town = matchNamibianTown(value);
            if (town && town !== value) onChange(town);
          }, 150);
        }}
        onKeyDown={(e) => {
          if (!open || !suggestions.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          if (e.key === 'Enter') { e.preventDefault(); choose(suggestions[active]); }
          if (e.key === 'Escape') setOpen(false);
        }}
        className={`${valid ? '' : 'border-red-400 dark:border-red-500'} w-full`}
      />
      {open && suggestions.length > 0 && (
        <ul role="listbox" className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg text-sm">
          {suggestions.map((town, i) => (
            <li
              key={town}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); choose(town); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer ${i === active ? 'bg-blue-50 dark:bg-slate-600 text-blue-700 dark:text-blue-200' : 'text-gray-800 dark:text-slate-100'}`}
            >
              {town}
            </li>
          ))}
        </ul>
      )}
      {!valid && String(value || '').trim() !== '' && !open && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">Choose a town from the list.</p>
      )}
    </div>
  );
};

export default TownInput;
